import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent, QuoteUpdateEvent } from '@theme/events';
import { QuoteStorage } from '@theme/quote-storage';

/**
 * A custom element that displays a cart or quote icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  get #isQuoteMode() {
    return this.dataset.quoteMode === 'true';
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.#isQuoteMode) {
      document.addEventListener(ThemeEvents.quoteUpdate, this.onQuoteUpdate);
      this.ensureQuoteBubbleIsCorrect();
    } else {
      document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    }

    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.removeEventListener(ThemeEvents.quoteUpdate, this.onQuoteUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * @param {PageTransitionEvent} event
   */
  onPageShow = (event) => {
    if (event.persisted) {
      if (this.#isQuoteMode) {
        this.ensureQuoteBubbleIsCorrect();
      } else {
        this.ensureCartBubbleIsCorrect();
      }
    }
  };

  /**
   * @param {CartUpdateEvent} event
   */
  onCartUpdate = async (event) => {
    if (this.#isQuoteMode) return;

    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * @param {QuoteUpdateEvent} event
   */
  onQuoteUpdate = async () => {
    if (!this.#isQuoteMode) return;

    this.renderCartBubble(QuoteStorage.getTotalQuantity(), false);
  };

  /**
   * @param {number} itemCount
   * @param {boolean} comingFromProductForm
   * @param {boolean} [animate=true]
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    const storageKey = this.#isQuoteMode ? 'quote-count' : 'cart-count';

    sessionStorage.setItem(
      storageKey,
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  ensureCartBubbleIsCorrect = () => {
    if (this.#isQuoteMode) return;
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (value === visibleCount) return;

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };

  ensureQuoteBubbleIsCorrect = () => {
    if (!this.#isQuoteMode) return;
    if (!this.refs.cartBubbleCount) return;

    const totalQuantity = QuoteStorage.getTotalQuantity();
    const visibleCount = parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);

    if (totalQuantity !== visibleCount) {
      this.renderCartBubble(totalQuantity, false, false);
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
